import React from "react";
import { Bell } from "lucide-react";

export default function ReviewStep({ 
  dashboardData, 
  awsAccounts, 
  regions, 
  collapsedNamespaces, 
  toggleNamespaceCollapse,
  isEditingDashboard 
}) {
  // Helper function to get the comparison operator symbol
  const getComparisonSymbol = (operator) => {
    switch (operator) {
      case "GreaterThanOrEqualToThreshold": return "≥";
      case "GreaterThanThreshold": return ">";
      case "LessThanThreshold": return "<";
      case "LessThanOrEqualToThreshold": return "≤";
      default: return operator;
    }
  };

  // Helper function to get all individual alarms for display (matching NamespacesStep logic)
  const getIndividualAlarms = (alarmConfig) => {
    if (!alarmConfig || !alarmConfig.alarms) return [];
    
    const individualAlarms = [];
    
    alarmConfig.alarms.forEach((alarm) => {
      if (alarm.resourceNames && alarm.resourceNames.length > 0) {
        // Create separate alarm for each resource
        alarm.resourceNames.forEach((resourceName) => {
          individualAlarms.push({
            ...alarm,
            resourceNames: [resourceName],
            alarmName: `dev-us-cloudlens-${resourceName}`
          });
        });
      } else {
        // All resources alarm or legacy format
        individualAlarms.push({
          ...alarm,
          resourceNames: [],
          alarmName: `dev-us-cloudlens-${alarm.metricName || 'all'}`
        });
      }
    });
    
    return individualAlarms;
  };

  // Helper function to get alarm display text (matching NamespacesStep logic)
  const getAlarmDisplayText = (alarm) => {
    const operatorSymbols = {
      "GreaterThanOrEqualToThreshold": "≥",
      "GreaterThanThreshold": ">",
      "LessThanThreshold": "<",
      "LessThanOrEqualToThreshold": "≤"
    };
    
    let resourceText = '';
    if (alarm.resourceNames && alarm.resourceNames.length > 0) {
      if (alarm.resourceNames.length === 1) {
        resourceText = ` (${alarm.resourceNames[0]})`;
      } else {
        resourceText = ` (${alarm.resourceNames.length} resources)`;
      }
    } else if (alarm.resourceName) {
      // Handle legacy single resourceName
      resourceText = ` (${alarm.resourceName})`;
    } else {
      resourceText = ' (All Resources)';
    }
    
    return `${alarm.statistic} ${operatorSymbols[alarm.comparisonOperator] || alarm.comparisonOperator} ${alarm.threshold}${resourceText}`;
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="bg-[#003366]/10 p-4 rounded-lg mb-6 border border-[#003366]/20">
        <h3 className="text-lg font-bold text-[#003366] mb-2">
          {isEditingDashboard ? "Review Your Changes" : "Review Your Configuration"}
        </h3>
        <p className="text-[#004488]">
          Please review your dashboard configuration before saving.
        </p>
      </div>

      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-100">
            <h4 className="text-sm font-medium text-[#003366] mb-1">
              Dashboard Name
              {isEditingDashboard && (
                <span className="text-gray-500 text-xs ml-2">(Cannot be modified)</span>
              )}
            </h4>
            <p className={`font-medium ${isEditingDashboard ? 'text-gray-600' : 'text-gray-900'}`}>
              {dashboardData.name}
            </p>
          </div>

          <div className="bg-gray-50 p-4 rounded-lg border border-gray-100">
            <h4 className="text-sm font-medium text-[#003366] mb-1">
              AWS Account
              {isEditingDashboard && (
                <span className="text-gray-500 text-xs ml-2">(Cannot be modified)</span>
              )}
            </h4>
            <p className={`font-medium ${isEditingDashboard ? 'text-gray-600' : 'text-gray-900'}`}>
              {awsAccounts.find(
                (a) => a.AccountNo === dashboardData.accountNo
              )?.AccountName || ""}{" "}
              ({dashboardData.accountNo})
            </p>
          </div>

          <div className="bg-gray-50 p-4 rounded-lg border border-gray-100">
            <h4 className="text-sm font-medium text-[#003366] mb-1">
              Region
              {isEditingDashboard && (
                <span className="text-gray-500 text-xs ml-2">(Cannot be modified)</span>
              )}
            </h4>
            <p className={`font-medium ${isEditingDashboard ? 'text-gray-600' : 'text-gray-900'}`}>
              {regions.find((r) => r.name === dashboardData.region)
                ?.description || dashboardData.region}
            </p>
          </div>

          <div className="bg-gray-50 p-4 rounded-lg border border-gray-100">
            <h4 className="text-sm font-medium text-[#003366] mb-1">
              Total Namespaces
            </h4>
            <p className="text-gray-900 font-medium">
              {dashboardData.namespaces.length}
            </p>
          </div>
        </div>

        <div className="mt-6">
          <h4 className="text-md font-medium text-[#003366] mb-3 border-b pb-2">
            Namespaces and Metrics Configuration
          </h4>
          
          {dashboardData.namespaces.length === 0 ? (
            <div className="text-center py-8 border border-dashed border-gray-200 rounded-md">
              <p className="text-gray-500">No namespaces added yet</p>
            </div>
          ) : (
            <div className="space-y-4">
              {dashboardData.namespaces.map((namespace, index) => (
                <div key={index} className="bg-white p-4 rounded-md border border-gray-200 shadow-sm">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center">
                      <h5 className="font-medium text-gray-800">
                        {namespace.name}
                      </h5>
                      {namespace.alarmConfigs && Object.keys(namespace.alarmConfigs).some(
                        metric => namespace.alarmConfigs[metric].enabled
                      ) && (
                        <span className="ml-2 text-[#003366]" title="Has alarm(s) configured">
                          <Bell size={14} />
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => toggleNamespaceCollapse(index)}
                      className="p-1 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                      aria-label={collapsedNamespaces.includes(index) ? "Expand" : "Collapse"}
                    >
                      {collapsedNamespaces.includes(index) ? (
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-800">
                          <polyline points="6 9 12 15 18 9"></polyline>
                        </svg>
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-800">
                          <polyline points="18 15 12 9 6 15"></polyline>
                        </svg>
                      )}
                    </button>
                  </div>

                  {!collapsedNamespaces.includes(index) && (
                    <div className="mt-3 space-y-4">
                      {/* Resource Names Section First */}
                      {namespace.resourceNames && namespace.resourceNames.length > 0 && (
                        <div className="bg-white p-3 rounded-md border border-gray-100">
                          <h5 className="font-medium text-[#003366] text-sm mb-2">Resource Names:</h5>
                          <div className="flex flex-wrap gap-1.5">
                            {namespace.resourceNames.map((resourceName) => (
                              <span
                                key={resourceName}
                                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-[#003366]/10 text-[#003366]"
                                title={resourceName}
                              >
                                <span className="truncate max-w-32">{resourceName}</span>
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Metrics with Alarms Section */}
                      <div className="bg-white p-3 rounded-md border border-gray-100">
                        <h5 className="font-medium text-[#003366] text-sm mb-2">Metrics & Alarms:</h5>
                        <div className="space-y-4">
                          {namespace.metrics.map((metric) => {
                            const alarmConfig = namespace.alarmConfigs && namespace.alarmConfigs[metric];
                            const hasAlarm = alarmConfig && alarmConfig.enabled;
                            const individualAlarms = hasAlarm ? getIndividualAlarms(alarmConfig) : [];
                            
                            return (
                              <div key={metric} className="border-b border-gray-100 pb-4 last:border-0 last:pb-0">
                                <div className="flex items-center mb-2">
                                  <span
                                    className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-[#003366]/10 text-[#003366]"
                                  >
                                    {metric}
                                    {hasAlarm && (
                                      <Bell size={12} className="ml-1 text-[#003366]" />
                                    )}
                                    {individualAlarms.length > 1 && (
                                      <span className="ml-1 bg-[#003366] text-white rounded-full text-xxs px-1.5">
                                        {individualAlarms.length}
                                      </span>
                                    )}
                                  </span>
                                </div>
                                
                                {/* Alarm Details Table for this specific metric */}
                                {hasAlarm && individualAlarms.length > 0 && (
                                  <div className="pl-1.5">
                                    <div className="bg-[#003366]/5 p-3 rounded-lg border border-[#003366]/10">
                                      <h6 className="text-xs font-medium text-[#003366] mb-2 flex items-center">
                                        <Bell size={12} className="mr-1 text-[#003366]" />
                                        Alarm Configuration:
                                      </h6>
                                      <div className="overflow-x-auto">
                                        <table className="min-w-full text-xs">
                                          <thead>
                                            <tr className="border-b border-[#003366]/20">
                                              <th className="text-left py-2 pr-2 font-medium text-[#003366]">Statistic</th>
                                              <th className="text-left py-2 px-2 font-medium text-[#003366]">Condition</th>
                                              <th className="text-left py-2 px-2 font-medium text-[#003366]">Threshold</th>
                                              <th className="text-left py-2 pl-2 font-medium text-[#003366]">Resource Name</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {individualAlarms.map((alarm, alarmIndex) => (
                                              <tr key={alarmIndex} className="border-b border-[#003366]/10 last:border-0">
                                                <td className="py-2 pr-2">{alarm.statistic}</td>
                                                <td className="py-2 px-2">{getComparisonSymbol(alarm.comparisonOperator)}</td>
                                                <td className="py-2 px-2">{alarm.threshold}</td>
                                                <td className="py-2 pl-2">
                                                  {alarm.resourceNames && alarm.resourceNames.length > 0 ? (
                                                    <span className="inline-block bg-[#003366]/10 text-[#003366] rounded px-2 py-0.5 text-xs truncate max-w-32">
                                                      {alarm.resourceNames[0]}
                                                    </span>
                                                  ) : alarm.resourceName ? (
                                                    <span className="inline-block bg-[#003366]/10 text-[#003366] rounded px-2 py-0.5 text-xs truncate max-w-32">
                                                      {alarm.resourceName}
                                                    </span>
                                                  ) : (
                                                    <span className="text-gray-500">All Resources</span>
                                                  )}
                                                </td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    </div>
                                  </div>
                                )}
                                
                                {/* Show message if metric has no alarms */}
                                {!hasAlarm && (
                                  <div className="pl-1.5 text-xs text-gray-500">
                                    No alarms configured for this metric
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}